import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import ImportWithMappingDialog from '../../../components/common/ImportWithMappingDialog';
import { trainingHistoryImportService } from '../../../services/trainingHistoryImportService';
import { companyService } from '../../../services/companyService';
import type { ImportServiceAdapter, ImportedRecordRef, MappingColumnConfig } from '../../../types/import';

interface TrainingHistoryImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  /** Disparado após uma importação bem-sucedida, para atualizar a lista de logs — sem fechar o dialog. */
  onLogsShouldRefresh?: () => void;
}

const COLUMNS: MappingColumnConfig[] = [
  { key: 'tipo_treinamento', label: 'Tipo de Treinamento', valueType: 'string' },
];

const COLUMN_LABELS: Record<string, string> = {
  tipo_treinamento: 'Tipo de Treinamento',
};

const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const trainingHistoryImportAdapter: ImportServiceAdapter = {
  previewImport: (file, options) => {
    // PDF não tem etapa de mapeamento — o preview só sinaliza "sem termo a resolver" pra
    // que o hook compartilhado já parta direto pra executeImport, que faz a importação real.
    if (isPdfFile(file)) {
      return Promise.resolve({ totalRows: 1, validRows: 1, errors: [], unresolvedTerms: [], availableOptions: {} });
    }
    return trainingHistoryImportService.previewImport(file, options as { companyId?: number; autoCreateCatalogEntries?: boolean });
  },
  executeImport: async (file, mappings, options) => {
    if (isPdfFile(file)) {
      const pdfOptions = options as { companyId?: number; autoCreateCatalogEntries?: boolean };
      const [result] = await trainingHistoryImportService.importFromPdfs([file], pdfOptions);
      if (!result?.success) {
        return { imported: 0, errors: [{ row: 1, error: result?.error ?? 'Erro ao importar PDF' }] };
      }
      // O próprio PDF já fica anexado ao registro no servidor — não populamos importedRecords
      // pra não acionar o painel de "anexar PDF" (seria redundante aqui).
      return { imported: 1, errors: [], importedRecords: [] };
    }

    const result = await trainingHistoryImportService.importTrainingsHistory(file, {
      ...(options as { companyId?: number; autoCreateCatalogEntries?: boolean }),
      mappings: mappings as Record<string, Record<string, string | number>> | undefined,
    });
    return {
      imported: result.success,
      errors: result.errors.map(e => ({ row: e.line, error: e.error })),
      warnings: result.warnings?.map(w => ({ row: w.line, warning: w.warning })),
      importedRecords: result.importedRecords,
    };
  },
  downloadTemplate: (options) =>
    trainingHistoryImportService.downloadTemplate(options as { companyId?: number }),
};

const TrainingHistoryImportDialog: React.FC<TrainingHistoryImportDialogProps> = ({
  open,
  onClose,
  onImportComplete,
  onLogsShouldRefresh,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [companies, setCompanies] = useState<Array<{ id: number; name: string }>>([]);
  const [autoCreateCatalogEntries, setAutoCreateCatalogEntries] = useState(false);
  const [useAiExtraction, setUseAiExtraction] = useState(false);

  const [lastFile, setLastFile] = useState<File | null>(null);
  const [lastHadErrors, setLastHadErrors] = useState(false);
  const [errorsExportLoading, setErrorsExportLoading] = useState(false);

  const [importedRecords, setImportedRecords] = useState<ImportedRecordRef[]>([]);
  const [attachedRows, setAttachedRows] = useState<Record<number, boolean>>({});
  const [uploadingRow, setUploadingRow] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      companyService.getCompaniesForDropdown().then(setCompanies).catch(() => {});
    }
  }, [open]);

  const getImportOptions = useCallback(() => {
    const options: Record<string, unknown> = {};
    if (selectedCompanyId) options.companyId = Number(selectedCompanyId);
    if (autoCreateCatalogEntries) options.autoCreateCatalogEntries = true;
    if (useAiExtraction) options.useAiExtraction = true;
    return options;
  }, [selectedCompanyId, autoCreateCatalogEntries, useAiExtraction]);

  const handleFileSelected = useCallback((file: File) => {
    setLastFile(file);
    setLastHadErrors(false);
  }, []);

  const handleImportComplete = useCallback((result?: { errors: Array<{ row: number; error: string }>; importedRecords?: ImportedRecordRef[] }) => {
    setLastHadErrors(!!result && result.errors.length > 0);
    setImportedRecords(result?.importedRecords ?? []);
    setAttachedRows({});
    onImportComplete();
    onLogsShouldRefresh?.();
  }, [onImportComplete, onLogsShouldRefresh]);

  const handleClose = useCallback(() => {
    setSelectedCompanyId('');
    setAutoCreateCatalogEntries(false);
    setUseAiExtraction(false);
    setLastFile(null);
    setLastHadErrors(false);
    setImportedRecords([]);
    setAttachedRows({});
    onClose();
  }, [onClose]);

  const handleDownloadErrors = useCallback(async () => {
    if (!lastFile) return;
    setErrorsExportLoading(true);
    try {
      const blob = await trainingHistoryImportService.exportErrors(lastFile, getImportOptions());
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'erros-importacao-treinamentos.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // erro silencioso: o download simplesmente não acontece, não há dialog de erro
      // dedicado aqui pra não duplicar a área de erro do ImportWithMappingDialog
    } finally {
      setErrorsExportLoading(false);
    }
  }, [lastFile, getImportOptions]);

  const handleAttachFile = useCallback((record: ImportedRecordRef) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingRow(record.row);
    trainingHistoryImportService
      .uploadHistoryCertificate(record.id, file)
      .then(() => setAttachedRows(prev => ({ ...prev, [record.row]: true })))
      .catch(() => {})
      .finally(() => setUploadingRow(null));
  }, []);

  return (
    <>
      <ImportWithMappingDialog
        open={open}
        onClose={handleClose}
        onImportComplete={handleImportComplete}
        onFileSelected={handleFileSelected}
        title="Importar Histórico de Treinamentos"
        description="Selecione uma planilha Excel (.xlsx) com o histórico de treinamentos já concluídos, anteriores à adoção do sistema — ou envie diretamente o PDF de um único certificado."
        acceptedFileTypes=".xlsx,.xls,.pdf"
        acceptedFormatsLabel="Formatos aceitos: .xlsx, .xls (planilha) ou .pdf (um certificado por arquivo)"
        entityLabel="treinamento"
        templateFilename="template-importacao-historico-treinamentos.xlsx"
        adapter={trainingHistoryImportAdapter}
        columns={COLUMNS}
        columnLabels={COLUMN_LABELS}
        getImportOptions={getImportOptions}
        scopeSelector={
          <Box sx={{ mb: 2 }}>
            {companies.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Empresa para importação:</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={selectedCompanyId}
                    onChange={(e) => setSelectedCompanyId(e.target.value as number)}
                    label="Empresa"
                  >
                    {companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoCreateCatalogEntries}
                  onChange={(e) => setAutoCreateCatalogEntries(e.target.checked)}
                />
              }
              label="Cadastrar automaticamente tipos de treinamento não encontrados no catálogo"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={useAiExtraction}
                  onChange={(e) => setUseAiExtraction(e.target.checked)}
                />
              }
              label="Deixar a IA reconhecer os dados automaticamente (planilha em qualquer formato)"
            />
          </Box>
        }
      />

      {open && lastHadErrors && lastFile && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={errorsExportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownloadErrors}
            disabled={errorsExportLoading}
          >
            {errorsExportLoading ? 'Baixando...' : 'Baixar Linhas com Erro'}
          </Button>
        </Box>
      )}

      {open && importedRecords.length > 0 && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, right: 16, maxWidth: 480, zIndex: (theme) => theme.zIndex.modal + 1 }}>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, boxShadow: 4, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Anexar PDF aos registros importados
            </Typography>
            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
              {importedRecords.map((record) => (
                <ListItem
                  key={record.row}
                  secondaryAction={
                    attachedRows[record.row] ? (
                      <Tooltip title="Certificado anexado">
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : uploadingRow === record.row ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Tooltip title="Anexar PDF">
                        <IconButton size="small" component="label">
                          <AttachFileIcon fontSize="small" />
                          <input
                            type="file"
                            accept="application/pdf"
                            hidden
                            onChange={handleAttachFile(record)}
                          />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemText
                    primary={record.employeeName}
                    secondary={`Linha ${record.row}`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TrainingHistoryImportDialog;
